import { convert, LocalDate } from '@js-joda/core';
import { useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';

import { FormContextValues, useFormController } from '@dreryk/react-common';

import { visitPaths } from '~libs.desktop.shared.paths';

import { formatDateToUrl } from '../appointment/filters/AppointmentsFilterChips/DateChipFilter.utils';

import { calendarPanelStorage } from './CalendarController.storage';
import { CalendarMode, CalendarState } from './CalendarController.types';

type LastCalendarDateSearchParam = {
   getParam: () => Date | null;
   setParam: (date: Date) => void;
};

export const useVisibleCalendarDateSearchParam = (): LastCalendarDateSearchParam => {
   const location = useLocation();
   const history = useHistory();
   const { current: visibleCalendarDateSearchParam } = useRef(visitPaths.CALENDAR_DATE_SEARCH_PARAM);

   const setParam = useCallback(
      (currentDate: Date) => {
         const searchParams = new URLSearchParams(location.search);

         const savedDate = searchParams.get(visibleCalendarDateSearchParam);
         if (savedDate === formatDateToUrl(currentDate)) {
            return;
         }

         searchParams.set(visibleCalendarDateSearchParam, formatDateToUrl(currentDate));
         history.replace({
            search: searchParams.toString(),
            state: location.state,
         });
      },
      [history, location.search, location.state, visibleCalendarDateSearchParam]
   );

   const getParam = useCallback((): Date | null => {
      const searchParams = new URLSearchParams(location.search);
      const savedDate = searchParams.get(visibleCalendarDateSearchParam);
      const parsedDate = savedDate ? new Date(savedDate) : null;
      if (!parsedDate) {
         return null;
      }
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
   }, [location.search, visibleCalendarDateSearchParam]);

   return useMemo(
      () => ({
         getParam: getParam,
         setParam: setParam,
      }),
      [getParam, setParam]
   );
};

type CalendarControllerOptions = {
   forceSpecialistMode: boolean;
   initialCurrentDate: () => Date | null;
};

export const useCalendarController = (options: CalendarControllerOptions): FormContextValues<CalendarState> => {
   const getInitialCalendarMode = (): CalendarMode => {
      if (options.forceSpecialistMode) {
         return 'specialist';
      }

      return calendarPanelStorage.getMode() ?? 'specialist';
   };

   return useFormController<CalendarState>({
      mode: getInitialCalendarMode(),
      currentDate: options.initialCurrentDate() ?? convert(LocalDate.now()).toDate(),
   });
};
